import React, { useState, useMemo } from "react";
import { Search, Plus, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Searchable left-rail list of product types for the Manufacturer detail page.
 *
 * Replaces the old horizontal scrolling tab strip which became unusable
 * when manufacturers approached 30+ product types. On mobile (< md) the
 * sidebar collapses into a header with an expand/collapse toggle so it
 * doesn't dominate the screen.
 */
export default function ProductTypeSidebar({
  productTypes,
  activePtId,
  onSelect,
  onAdd,
}) {
  const [filter, setFilter] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return productTypes;
    return productTypes.filter((pt) =>
      (pt.name || "").toLowerCase().includes(q)
    );
  }, [productTypes, filter]);

  const activePt = productTypes.find((pt) => pt.id === activePtId);

  return (
    <aside
      className="md:w-72 md:flex-none border-b md:border-b-0 border-border bg-slate-50/60"
      data-testid="product-type-sidebar"
    >
      {/* Mobile header: shows active PT name + toggle */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-mono-data text-muted-foreground">
            Product type
          </div>
          <div className="text-sm font-semibold truncate">
            {activePt?.name || "Select a product type"}
          </div>
        </div>
        <button
          onClick={() => setMobileOpen((o) => !o)}
          className="ml-2 p-2 rounded-sm hover:bg-white"
          data-testid="product-type-sidebar-toggle"
          aria-label="Toggle product type list"
        >
          {mobileOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className={`${mobileOpen ? "block" : "hidden"} md:block`}>
        {/* Search filter */}
        <div className="p-3 border-b border-border bg-white md:bg-transparent">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter product types..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              data-testid="product-type-filter-input"
            />
          </div>
          {productTypes.length > 0 && (
            <div className="mt-2 text-[10px] uppercase tracking-widest font-mono-data text-muted-foreground">
              {filtered.length === productTypes.length
                ? `${productTypes.length} type${productTypes.length === 1 ? "" : "s"}`
                : `${filtered.length} of ${productTypes.length}`}
            </div>
          )}
        </div>

        {/* List */}
        <div className="md:max-h-[70vh] md:overflow-y-auto py-1">
          {filtered.length === 0 && filter && (
            <div className="px-4 py-6 text-xs text-muted-foreground italic text-center">
              No types match "{filter}"
            </div>
          )}
          {filtered.map((pt) => {
            const active = pt.id === activePtId;
            return (
              <button
                key={pt.id}
                onClick={() => {
                  onSelect(pt.id);
                  setMobileOpen(false);
                }}
                data-testid={`pt-tab-${pt.id}`}
                className={`group relative w-full text-left px-4 py-2.5 text-sm border-l-2 transition-colors ${
                  active
                    ? "bg-white border-l-amber-500 text-slate-900 font-medium"
                    : "border-l-transparent text-muted-foreground hover:bg-white hover:text-slate-900"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{pt.name}</span>
                  <span
                    className={`text-[10px] uppercase tracking-wider font-mono-data flex-none ${
                      active ? "text-slate-700" : "text-muted-foreground"
                    }`}
                  >
                    {pt.product_count ?? 0}
                  </span>
                </div>
              </button>
            );
          })}

          {/* Add new */}
          <button
            onClick={() => {
              onAdd?.();
              setMobileOpen(false);
            }}
            data-testid="add-product-type-btn"
            className="w-full text-left px-4 py-2.5 mt-1 text-sm font-medium text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1.5 border-t border-border"
          >
            <Plus className="w-4 h-4" /> Add product type
          </button>
        </div>
      </div>
    </aside>
  );
}
